/**
 * Aadhar OTP Actions
 * Handles OTP sending and verification for viewing encrypted Aadhar numbers
 */

import apiClient from "../services/apiClient";
import { URL_CONFIG } from "../config/urlConfig";
import { getUserData } from "../utils/authnicate";

/**
 * Send OTP to admin email for viewing encrypted Aadhar
 * @param {string} email - Admin email address
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const sendAadharOtpAction = async (email, isUpdated) => {
  try {
    const response = await apiClient.get(
      `${URL_CONFIG.ATP_API_BASE_URL}api/Onboarding/send-otp-for-encrypted-Value`,
      {
        params: { email, isUpdated },
      },
    );

    if (response.status === 200) {
      return {
        success: true,
        message: response.data?.message || "OTP sent successfully",
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to send OTP",
      };
    }
  } catch (error) {
    console.error("[sendAadharOtpAction] Error:", error);
    return {
      success: false,
      message:
        error.response?.data?.message || error.message || "Failed to send OTP",
    };
  }
};

/**
 * Verify OTP and get original (decrypted) Aadhar value
 * @param {Object} params - Verification parameters
 * @param {number} params.entityPrimaryId - Entity primary ID
 * @param {string} params.documentName - Document name (e.g., "aadharCardNumber")
 * @param {string} params.email - Admin email address
 * @param {string} params.otp - OTP code
 * @returns {Promise<{success: boolean, message: string, originalValue: string}>}
 */
export const verifyAadharOtpAction = async ({
  entityPrimaryId,
  documentName,
  email,
  otp,
}) => {
  try {
    const payload = {
      entityPrimaryId: parseInt(entityPrimaryId, 10) || 0,
      documentName: documentName || "",
      email: email || "",
      otp: otp || "",
    };

    const response = await apiClient.post(
      `${URL_CONFIG.ATP_API_BASE_URL}api/Onboarding/get-original-value`,
      payload,
    );

    if (response.status === 200 && response.data) {
      const { success, value, message } = response.data;

      if (success === true) {
        return {
          success: true,
          message: message || "OTP verified successfully",
          originalValue: value || "", // API returns decrypted value in "value" field
          data: response.data,
        };
      }

      // success: false — invalid OTP or expired
      return {
        success: false,
        message: message || "Invalid OTP or OTP expired",
        originalValue: null,
      };
    }

    return {
      success: false,
      message: "Invalid OTP",
      originalValue: null,
    };
  } catch (error) {
    console.error("[verifyAadharOtpAction] Error:", error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        "Failed to verify OTP",
      originalValue: null,
    };
  }
};

/**
 * Map field names to API document names
 * @param {string} fieldName - Internal field name
 * @returns {string} - API document name
 */
export const getDocumentNameForField = (fieldName) => {
  const fieldToDocumentMap = {
    // KYC-detail page (UploadDetails) field names → API document names
    doc_aadhar_number: "aadharCardNumber",
    doc_aadharBoth_numbers: "aadharNumberOfBothPartners",
    doc_aadharBothSecondary_numbers: "aadharNumberOfBothPartnersSecond",
    doc_aadharTrustee_number: "trusteeAadharNumber",
    doc_directorAadhar_number: "directorAadharNumber",
    // All List page (EditClientModal) field names — already match API names
    aadharCardNumber: "aadharCardNumber",
    aadharNumberOfBothPartners: "aadharNumberOfBothPartners",
    aadharNumberOfBothPartnersSecond: "aadharNumberOfBothPartnersSecond",
    aadharNumberOfTrustee: "trusteeAadharNumber",
    directorAadharNumber: "directorAadharNumber",
  };

  return fieldToDocumentMap[fieldName] || fieldName;
};

/**
 * Update encrypted Aadhar data after OTP verification
 * POST api/Onboarding/update-encrypted-data
 *
 * @param {Object} params
 * @param {number} params.entityPrimaryId
 * @param {string} params.documentName  - e.g. "aadharCardNumber"
 * @param {string} params.encryptedData - new Aadhar number entered by admin
 * @param {string} params.email         - admin email
 * @param {string} params.otp           - verified OTP
 * @param {string} params.modifiedBy    - logged-in user email/name
 */
export const updateAadharEncryptedAction = async ({
  entityPrimaryId,
  documentName,
  encryptedData,
  email,
  otp,
  modifiedBy = "",
}) => {
  try {
    const { ATP_API_URL } = await import("../config/apiConfig");

    // Auto-resolve modifiedBy from logged-in user
    const loggedInUser = getUserData();
    const resolvedModifiedBy =
      modifiedBy ||
      loggedInUser?.email ||
      loggedInUser?.name ||
      loggedInUser?.username ||
      "Admin";

    const payload = {
      entityPrimaryId: parseInt(entityPrimaryId, 10) || 0,
      documentName: documentName || "",
      encryptedData: encryptedData || "",
      email: email || "",
      otp: otp || "",
      modifiedBy: resolvedModifiedBy,
    };

    const response = await apiClient.post(
      ATP_API_URL.UPDATE_ENCRYPTED_DATA,
      payload,
    );

    const status = response.data.success;
    if (status === true) {
      return {
        success: true,
        message: response.data?.message || "Aadhar updated successfully",
        data: response.data,
      };
    }
    return {
      success: false,
      message: response.data?.message || "Failed to update Aadhar",
    };
  } catch (error) {
    console.error("[updateAadharEncryptedAction] Error:", error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        "Failed to update Aadhar",
    };
  }
};
